import { ReactNode, useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzSummaryCreateItemWithNewReportMutation } from "@/services/backend/htz/summary/api/summary-api";

/**
 * SummaryItemReportCreateDialog creates a new, empty Anlagenbericht for the
 * summary and adds an item summarizing it. The report belongs to the same
 * process, company, branch, customer and service object as the summary.
 */
export function SummaryItemReportCreateDialog({
  summaryId,
  trigger,
  onCreated,
  disabled,
}: {
  summaryId: UUID;
  trigger?: ReactNode;
  onCreated?: (itemId: UUID, reportId: UUID) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [create, { isLoading, error, reset }] =
    useHtzSummaryCreateItemWithNewReportMutation();
  const { resetField } = useErrValidationFailedContext();

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    const itemId = v4();
    const reportId = v4();
    // A negative order appends the item behind the existing ones.
    create({ id: summaryId, itemId, reportId, order: -1 })
      .unwrap()
      .then(() => {
        toast.success(t("Anlagenbericht erstellt"));
        // "items" holds the error of a summary without items.
        resetField("items");
        reset();
        setOpen(false);
        onCreated?.(itemId, reportId);
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger || (
        <CommonDialogTrigger
          icon={Plus}
          label={t("Neuer Anlagenbericht")}
          size="sm"
          disabled={disabled}
        />
      )}
      <DialogContent>
        <CommonDialogHeader
          title={t("Anlagenbericht erstellen")}
          description={t(
            "Ein neuer Anlagenbericht wird für den Inspektionsbericht erstellt und als Zeile hinzugefügt.",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}
